import React, { useMemo, useState, useEffect, useContext, useRef, useImperativeHandle } from 'react';
import { useLocalizedStrings } from '../Localization';
import { usePost } from '../API';
import InterfaceContext from '../Context';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { TextArea, cn } from '@aidkitorg/component-library';
import { Dropdown } from './Dropdown';
import { detectDirection } from '@aidkitorg/types/lib/utils/textDirection';

/**
 * The content of a message that may contain subsurvey links.
 * Either a plain string or an array of string segments and link objects.
 */
export type MessageContent = string | (string | { subsurvey: string; label: string })[];

// Styles applied identically to the highlight overlay and the textarea.
// Both being in the same CSS grid cell ensures they are the same size;
// identical styles ensure their text lands at the same pixel positions.
const TOKEN_STYLE: React.CSSProperties = {
  fontFamily: 'inherit',
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: '1.5',
  letterSpacing: 'normal',
  padding: '6px 12px',
  margin: 0,
  minHeight: '80px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  boxSizing: 'border-box',
  border: 'none',
  gridArea: '1/1',
};

// Matches [[label]] tokens. The capturing group preserves delimiters when used with split().
export const TOKEN_REGEX = /(\[\[[^\]]+\]\])/;

// Converts a message string containing [[label]] tokens into the
// Message array the backend expects, so links get resolved to short URLs.
export function parseTokenMessage(text: string, linkMap: Record<string, string>): MessageContent {
  return text.split(TOKEN_REGEX).map(part => {
    const label = extractSubsurveyLabelFromToken(part);
    return label ? { subsurvey: linkMap[label] ?? label, label } : part;
  });
}

export function extractSubsurveyLabelFromToken(token: string) {
  if (token.match(TOKEN_REGEX)) {
    return token.slice(2, -2);
  }
  return null;
}

function renderHighlightedText(text: string, linkMap: Record<string, string>): React.ReactNode {
  return text.split(TOKEN_REGEX).map((part, i) => {
    const label = extractSubsurveyLabelFromToken(part);
    const backgroundStyling = !!label
      ? { background: label in linkMap ? '#e0e7ff' : '#fee2e2', borderRadius: '3px' }
      : {};

    return <span key={i} style={{ color: 'transparent', ...backgroundStyling }}>{part}</span>;
  });
}

export function getClampedTextAreaContentHeight(textarea:HTMLTextAreaElement, minHeight: number) { 
  textarea.style.height = 'auto';
  return Math.max(minHeight, Math.min(textarea.scrollHeight, window.innerHeight / 3));
}

export const CommsMessageInput = React.forwardRef<HTMLDivElement, {
  message: string;
  onChange: (value: string) => void;
  disabled: boolean;
  linkMap: Record<string, string>;
  placeholder?: string;
  className?: string;
  dynamicallyResizeTextArea?: boolean;
  // Pins the input to this height (px) instead of auto-growing; longer content scrolls
  fixedHeight?: number;
}>(function CommsMessageInput({ message, onChange, disabled, linkMap, placeholder, className, dynamicallyResizeTextArea, fixedHeight }, ref) {
      const L = useLocalizedStrings();
      const overlayRef = useRef<HTMLDivElement>(null);
      const textareaRef = useRef<HTMLTextAreaElement>(null);
      // The container needs its own ref so the auto-grow effect below can size it.
      // The forwarded ref may be a callback, so it can't be read from directly.
      const containerRef = useRef<HTMLDivElement>(null);
      useImperativeHandle(ref, () => containerRef.current!, []);

      const syncScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
        if (overlayRef.current) overlayRef.current.scrollTop = e.currentTarget.scrollTop;
      };

      // Auto-grow height as content increases, capped at a third of viewport height.
      // The container reuses the textarea's measured height rather than measuring
      // itself: the overlay and textarea share TOKEN_STYLE in a single grid cell, so
      // they are the same size by construction, and reusing it avoids a second reflow.
      useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea || (!dynamicallyResizeTextArea && !fixedHeight)) return;
        const height = fixedHeight ?? getClampedTextAreaContentHeight(textarea, 80);
        textarea.style.height = `${height}px`;
        if (containerRef.current) containerRef.current.style.height = `${height}px`;
      }, [message, dynamicallyResizeTextArea, fixedHeight]);

      // if the height is managed by external factors, don't make it resizeable heres
      const heightIsManaged = !!(dynamicallyResizeTextArea || fixedHeight);

      return (
        <div
          ref={containerRef}
          className={cn(
            "border border-gray-300 rounded focus-within:!border-[#80bdff] focus-within:shadow-[0_0_0_.2rem_rgba(0,123,255,.25)]",
            className,
          )}
          style={{ display: 'grid', background: 'white' }}
        >
          <div
            ref={overlayRef}
            aria-hidden
            style={{ ...TOKEN_STYLE, pointerEvents: 'none', userSelect: 'none', overflow: 'hidden' }}
          >
            {renderHighlightedText(message, linkMap)}
          </div>
          <TextArea
            ref={textareaRef}
            label={L.applicant.comms.message_to_send}
            showLabel={false}
            value={message}
            onChange={(e) => onChange(e.target.value)}
            onScroll={syncScroll}
            disabled={disabled}
            placeholder={placeholder}
            spellCheck={true}
            dir={detectDirection(message)}
            containerStyle={{ gridArea: '1/1' }}
            transparentBackground
            textareaClassName="border-0 px-[12px]"
            style={{ ...TOKEN_STYLE, outline: 'none', resize: heightIsManaged ? 'none' : 'vertical' }}
          />
        </div>
      );
    });

export function InsertSubsurveyLinkButton({
  applicant,
  onInsert,
  disabled,
}: {
  applicant: string | undefined;
  onInsert: (subsurvey: string, label: string) => void;
  disabled: boolean;
}) {
  const L = useLocalizedStrings();
  const getSubsurveys = usePost('/applicant/get_exposed_subsurveys', { suppressErrorDisplay: true });
  const [subsurveys, setSubsurveys] = useState<{ part: string; subsurveyNameTranslations: Record<string, string> }[]>([]);
  const [loaded, setLoaded] = useState(false);
  const context = useContext(InterfaceContext);

  useAsyncEffect(async () => {
    if (!applicant) {
      setSubsurveys([]);
      setLoaded(false);
      return;
    }

    setLoaded(false);
    setSubsurveys([]);

    try {
      const response = await getSubsurveys({ applicant });
      if (response?.subsurveys?.length) {
        setSubsurveys(response.subsurveys);
      }
    } catch {
    } finally {
      setLoaded(true);
    }
  }, [applicant]);

  const options = useMemo(() => subsurveys.map((s) => {
    const label =
      s.subsurveyNameTranslations[context.lang] ||
      s.subsurveyNameTranslations['en'] ||
      s.part;
    return { label, callback: () => onInsert(s.part, label) };
  }), [subsurveys, context.lang, onInsert]);

  if (!loaded || subsurveys.length === 0) return null;

  return (
    <Dropdown
      label={L.applicant.comms.insert_survey_link}
      color='white'
      options={options}
      disabled={disabled}
      usePopper
    />
  );
}
